import React, { useEffect, useRef, useState } from 'react';
import { boards } from '../data/mock';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { BookOpen, GraduationCap, School, ChevronRight, CheckCircle2 } from 'lucide-react';

const iconMap = {
  BookOpen,
  GraduationCap,
  School
};

const colorMap = {
  teal: {
    bg: 'bg-teal-50',
    border: 'border-teal-100',
    icon: 'bg-teal-100 text-teal-600',
    badge: 'bg-teal-500',
    hover: 'hover:border-teal-200 hover:shadow-teal-500/5',
    check: 'text-teal-500',
    accent: 'from-teal-500 to-teal-600'
  },
  amber: {
    bg: 'bg-amber-50',
    border: 'border-amber-100',
    icon: 'bg-amber-100 text-amber-600',
    badge: 'bg-amber-500',
    hover: 'hover:border-amber-200 hover:shadow-amber-500/5',
    check: 'text-amber-500',
    accent: 'from-amber-500 to-amber-600'
  },
  rose: {
    bg: 'bg-rose-50',
    border: 'border-rose-100',
    icon: 'bg-rose-100 text-rose-600',
    badge: 'bg-rose-500',
    hover: 'hover:border-rose-200 hover:shadow-rose-500/5',
    check: 'text-rose-500',
    accent: 'from-rose-500 to-rose-600'
  }
};

export const Programs = () => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.disconnect(); } },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="programs" ref={ref} className="relative py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className={`text-center max-w-2xl mx-auto mb-16 scroll-fade-in ${isVisible ? 'is-visible' : ''}`}>
          <Badge className="bg-teal-50 text-teal-700 border-teal-200 px-4 py-1.5 text-sm font-medium mb-6">
            Our Programs
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-5">
            Board-Wise{' '}
            <span className="text-teal-600">Coaching Programs</span>
          </h2>
          <p className="text-lg text-slate-500">
            Tailored coaching programs for every board, designed by expert educators who understand each curriculum deeply.
          </p>
        </div>

        {/* Cards */}
        <div className="grid md:grid-cols-3 gap-8">
          {boards.map((board, index) => {
            const Icon = iconMap[board.icon];
            const colors = colorMap[board.color];
            return (
              <Card
                key={board.id}
                className={`relative overflow-hidden border-2 ${colors.border} ${colors.hover} hover:shadow-xl transition-all duration-500 hover:-translate-y-1 group scroll-fade-in ${isVisible ? 'is-visible' : ''}`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                {/* Top accent bar */}
                <div className={`h-1 w-full bg-gradient-to-r ${colors.accent}`} />

                <CardContent className="p-8">
                  {/* Icon & Name */}
                  <div className="flex items-start gap-4 mb-6">
                    <div className={`w-14 h-14 rounded-2xl ${colors.icon} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-500`}>
                      {Icon && <Icon className="w-7 h-7" />}
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-slate-900">{board.name}</h3>
                      <p className="text-sm text-slate-500">{board.fullName}</p>
                    </div>
                  </div>

                  {/* Classes badge */}
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold text-white ${colors.badge} mb-5`}>
                    {board.classes}
                  </span>

                  {/* Description */}
                  <p className="text-slate-600 leading-relaxed mb-6">
                    {board.description}
                  </p>

                  {/* Highlights */}
                  <div className="space-y-3">
                    {board.highlights.map((highlight, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <CheckCircle2 className={`w-4 h-4 ${colors.check} flex-shrink-0`} />
                        <span className="text-sm text-slate-600">{highlight}</span>
                      </div>
                    ))}
                  </div>

                  {/* Learn more */}
                  <div className="mt-8 pt-6 border-t border-slate-100">
                    <button
                      onClick={() => {
                        const el = document.getElementById('contact');
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }}
                      className="flex items-center text-sm font-semibold text-slate-800 group/btn hover:text-teal-600 transition-colors duration-300"
                    >
                      Enquire Now
                      <ChevronRight className="w-4 h-4 ml-1 group-hover/btn:translate-x-1 transition-transform duration-300" />
                    </button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};
